<script setup lang="ts">
import YscInputGroup from './YscInputGroup.vue'
import { defineAsyncComponent, ref, watch, computed, defineOptions } from '#imports'

const YscIconsEye = defineAsyncComponent(() => import('./YscIconsEye.vue'))
const YscIconsEyeOff = defineAsyncComponent(() => import('./YscIconsEyeOff.vue'))

defineOptions({ inheritAttrs: false })
type ModelValue = string | number | null
interface Props {
  modelValue: ModelValue
  id: string
  type?: 'text' | 'number' | 'password' | 'email' | 'date'
  disabled?: boolean
  required?: boolean
  fullWidth?: boolean
  isActionContext?: boolean
  reverseContext?: boolean
  removeBorder?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  modelValue: null,
  disabled: false,
  required: false,
  fullWidth: false,
  isActionContext: false,
  reverseContext: false,
  removeBorder: false,
})
const emits = defineEmits<{
  'update:model-value': [ModelValue]
  'click': [Event]
}>()

const localValue = ref<ModelValue>(null)

watch(localValue, (localVal) => {
  // prevents from infinite watch loop
  if (props.modelValue === localVal) {
    return
  }

  let toEmit = localVal
  if (typeof toEmit === 'string' && toEmit.length === 0) {
    toEmit = null
  }

  emits('update:model-value', toEmit)
})

watch(
  () => props.modelValue,
  (modelVal) => {
    // prevents from infinite watch loop
    if (modelVal === localValue.value) {
      return
    }

    localValue.value = modelVal
  },
  { immediate: true },
)

const show = ref(false)
const isPassword = computed(() => {
  return props.type === 'password'
})
const inputType = computed(() => {
  return isPassword.value && show.value ? 'text' : props.type
})

const onClickContext = (evt: Event) => {
  if (isPassword.value) {
    show.value = !show.value
    return
  }
  emits('click', evt)
}
</script>

<template>
  <YscInputGroup
    :for="props.id"
    :required="props.required"
    :disabled="props.disabled"
  >
    <template
      v-if="$slots.default"
      #default
    >
      <slot />
    </template>

    <template #inputBlock>
      <div
        class="relative flex max-w-full items-stretch overflow-hidden rounded-sm focus-within:border-gray-400"
        :class="[
          props.removeBorder ? 'border-none' : 'border',
          props.disabled ? 'border-gray-200 bg-gray-100' : 'border-gray-300 bg-white',
          { 'w-max': !props.fullWidth },
          { 'flex-row-reverse': props.reverseContext },
        ]"
      >
        <input
          :id="props.id"
          ref="input"
          v-model="localValue"
          :disabled="props.disabled"
          :required="props.required"
          :name="props.id"
          :type="inputType"
          v-bind="$attrs"
          :class="[
            props.disabled ? 'cursor-not-allowed' : 'cursor-text',
            { '!p-0': props.removeBorder },
            { 'pl-0': props.isActionContext && props.reverseContext },
            { 'pr-0': props.isActionContext && !props.reverseContext },
            { 'w-full': props.fullWidth },
            { 'text-center': inputType === 'date' },
          ]"
          class="min-w-0 appearance-none bg-transparent px-4 py-2.5 text-base leading-normal outline-none disabled:text-gray-400"
        >
        <template v-if="$slots.context || isPassword">
          <button
            v-if="props.isActionContext || isPassword"
            :disabled="props.disabled"
            type="button"
            class="flex appearance-none items-center rounded-sm px-4 text-gray-500 outline-none focus-visible:ring-2 focus-visible:ring-inset disabled:cursor-not-allowed disabled:hover:text-gray-400"
            @click.prevent.stop="onClickContext"
          >
            <YscIconsEye
              v-if="isPassword && show"
              class="h-6 w-6"
            />
            <YscIconsEyeOff
              v-else-if="isPassword"
              class="h-6 w-6"
            />
            <slot
              v-else
              name="context"
            />
          </button>
          <span
            v-else
            class="flex items-center bg-gray-50 px-4 text-gray-500"
          >
            <slot name="context" />
          </span>
        </template>
      </div>
    </template>

    <template
      v-if="$slots.description"
      #description
    >
      <slot name="description" />
    </template>

    <template
      v-if="$slots.error"
      #error
    >
      <slot name="error" />
    </template>
  </YscInputGroup>
</template>

<style scoped>
input[type=number],input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;-moz-appearance:textfield!important;margin:0}
</style>
